import type { DocumentFont } from '@flobi/artwork-core';
import type { LibraryFont } from '../../services/fonts';

/**
 * What the font picker lists, worked out apart from the picker so it can be tested.
 *
 * Flobi Fonts keeps one row per FILE — Instrument Sans Regular, Instrument Sans Bold — and
 * a person picks a FAMILY, so the files are gathered into families here. The families the
 * file's brand uses come first, then the ones its project uses, then the rest of the
 * library.
 */

export type FontFilter = 'all' | 'serif' | 'sans' | 'display' | 'script' | 'mono';

export const FONT_FILTERS: readonly (readonly [FontFilter, string])[] = [
  ['all', 'All Styles'],
  ['serif', 'Serif'],
  ['sans', 'Sans Serif'],
  ['display', 'Display'],
  ['script', 'Script'],
  ['mono', 'Monospace'],
];

/** What Google's catalogue calls each kind. */
export const GOOGLE_CATEGORY: Record<Exclude<FontFilter, 'all'>, string> = { serif: 'Serif', sans: 'Sans Serif', display: 'Display', script: 'Handwriting', mono: 'Monospace' };

/** What Flobi Fonts calls them: it tells a slab from a serif and a script from a hand, which a picker need not. */
const LIBRARY_KIND: Record<string, Exclude<FontFilter, 'all'>> = { serif: 'serif', slab: 'serif', sans: 'sans', display: 'display', symbol: 'display', script: 'script', handwritten: 'script', mono: 'mono' };

export interface LibraryFamily {
  readonly family: string;
  readonly faces: readonly LibraryFont[];
  /** Null when Flobi Fonts has not classified it: listed under All Styles only. */
  readonly kind: Exclude<FontFilter, 'all'> | null;
  readonly brands: ReadonlySet<string>;
  readonly projects: ReadonlySet<string>;
}

const WEIGHT_WORD = / (thin|hairline|extra ?light|ultra ?light|light|medium|semi ?bold|demi ?bold|extra ?bold|ultra ?bold|bold|black|heavy)$/i;

/**
 * The family a face belongs to. Google's static files name the weight into the family
 * ("Geist SemiBold", style Regular, weight 600), which listed one family as several.
 */
export function familyOf(font: Pick<LibraryFont, 'family' | 'weight'>): string {
  const name = font.family.trim();
  return font.weight !== 400 && WEIGHT_WORD.test(name) ? name.replace(WEIGHT_WORD, '') : name;
}

/** The library's files as families, in the order their first file came. */
export function libraryFamilies(fonts: readonly LibraryFont[]): LibraryFamily[] {
  const byName = new Map<string, { family: string; faces: LibraryFont[]; kinds: Map<string, number>; brands: Set<string>; projects: Set<string> }>();
  const seen = new Set<string>();
  for (const font of fonts) {
    // A page asked for twice is not a second face.
    if (seen.has(font.id)) continue;
    seen.add(font.id);
    const family = familyOf(font);
    const key = family.toLowerCase();
    let group = byName.get(key);
    if (!group) byName.set(key, (group = { family, faces: [], kinds: new Map(), brands: new Set(), projects: new Set() }));
    group.faces.push(font);
    const kind = font.classification ? LIBRARY_KIND[font.classification] : undefined;
    if (kind) group.kinds.set(kind, (group.kinds.get(kind) ?? 0) + 1);
    for (const id of font.brands ?? []) group.brands.add(id);
    for (const id of font.projects ?? []) group.projects.add(id);
  }
  return [...byName.values()].map((group) => ({
    family: group.family,
    faces: group.faces,
    // What most of its faces are read as: one odd weight does not move the family.
    kind: ([...group.kinds.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] as LibraryFamily['kind']) ?? null,
    brands: group.brands,
    projects: group.projects,
  }));
}

const plain = (text: string) => text.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

/** Whether every word typed is somewhere in the name, whatever the case, accents or punctuation. */
export function nameMatches(name: string, query: string): boolean {
  const words = plain(query).split(' ').filter(Boolean);
  const hay = plain(name);
  return words.every((word) => hay.includes(word));
}

export type FlobiRow =
  /** A font of the brand's kit: ready to write into the file as it is. */
  | { readonly from: 'kit'; readonly family: string; readonly font: DocumentFont; readonly kind: LibraryFamily['kind']; readonly styles: number }
  | { readonly from: 'library'; readonly family: string; readonly entry: LibraryFamily; readonly kind: LibraryFamily['kind']; readonly styles: number };

export interface FlobiSection {
  /** Whose fonts these are. Null when there is only one section and it needs no name. */
  readonly title: string | null;
  readonly rows: readonly FlobiRow[];
}

/**
 * The Flobi Fonts tab: the brand's fonts, the project's, then everything else — each
 * family once, in the first place it belongs.
 */
export function flobiSections(input: {
  library: readonly LibraryFont[];
  /** The fonts of the brand in force, as its kit hands them over. */
  kitFonts: readonly DocumentFont[];
  brand: { id: string; name: string } | null;
  project: { id: string; name: string } | null;
  query: string;
  filter: FontFilter;
}): FlobiSection[] {
  const families = libraryFamilies(input.library);
  const byName = new Map(families.map((family) => [family.family.toLowerCase(), family]));
  const taken = new Set<string>();
  const shows = (family: string, kind: LibraryFamily['kind']) => nameMatches(family, input.query) && (input.filter === 'all' || kind === input.filter);
  const fromLibrary = (entry: LibraryFamily): FlobiRow => ({ from: 'library', family: entry.family, entry, kind: entry.kind, styles: entry.faces.length });
  const take = (rows: FlobiRow[]) =>
    rows.filter((row) => {
      const key = row.family.toLowerCase();
      if (taken.has(key)) return false;
      taken.add(key);
      return shows(row.family, row.kind);
    });

  const brandRows: FlobiRow[] = input.brand
    ? [
        ...input.kitFonts.map((font): FlobiRow => ({ from: 'kit', family: font.family, font, kind: byName.get(font.family.toLowerCase())?.kind ?? null, styles: font.faces.length })),
        ...families.filter((entry) => entry.brands.has(input.brand!.id)).map(fromLibrary),
      ]
    : [];
  const brand = take(brandRows);
  const project = take(input.project ? families.filter((entry) => entry.projects.has(input.project!.id)).map(fromLibrary) : []);
  const rest = take(families.map(fromLibrary));

  const sections: FlobiSection[] = [];
  if (brand.length > 0) sections.push({ title: input.brand!.name, rows: brand });
  if (project.length > 0) sections.push({ title: input.project!.name, rows: project });
  if (rest.length > 0) sections.push({ title: sections.length > 0 ? 'Other Fonts' : null, rows: rest });
  return sections;
}

/** Which tab holds the font the text is set in, when the file says: the tab the picker opens on. */
export function tabOf(value: string, inDocument: readonly DocumentFont[]): 'flobi' | 'google' | null {
  const source = inDocument.find((font) => font.family === value)?.source;
  if (!source) return null;
  return source === 'google' || source === 'builtin' ? 'google' : 'flobi';
}
